/** Practice name for "Generated by" on consent list, PDF footer, etc. */
export function resolveConsentGeneratedByName(consent) {
  if (!consent) return null;
  const clinic = (consent.clinicName || '').trim();
  if (clinic) return clinic;
  const fromInfo = (consent.generatedByInfo?.name || '').trim();
  if (fromInfo && !isClinicNameLabel(fromInfo, clinic)) return fromInfo;
  return fromInfo || consent.dentistInfo?.name || null;
}

/** Initials under "Generated by" — only when they match the displayed name. */
export function resolveConsentGeneratedByInitials(consent) {
  const displayName = resolveConsentGeneratedByName(consent);
  if (!displayName || !consent?.generatedByInfo?.name) return null;
  if (consent.generatedByInfo.name.trim() !== displayName) return null;
  return consent.generatedByInfo.initials || null;
}

const NAME_TITLES = new Set([
  'dr', 'mr', 'mrs', 'miss', 'ms', 'mx', 'prof', 'professor', 'drc',
]);

function stripNameTitles(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !NAME_TITLES.has(part.replace(/\./g, '').toLowerCase()))
    .join(' ');
}

/** First + last initial (max 2), ignoring Dr/Mr/Mrs etc. */
export function initialsFromPersonName(name) {
  const cleaned = stripNameTitles(name);
  if (!cleaned) return '';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

const CLINICIAN_AVATAR_PALETTE = [
  { bg: 'bg-violet-100', text: 'text-violet-800', ring: 'ring-violet-200' },
  { bg: 'bg-sky-100', text: 'text-sky-800', ring: 'ring-sky-200' },
  { bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-200' },
  { bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200' },
  { bg: 'bg-rose-100', text: 'text-rose-800', ring: 'ring-rose-200' },
  { bg: 'bg-teal-100', text: 'text-teal-800', ring: 'ring-teal-200' },
  { bg: 'bg-indigo-100', text: 'text-indigo-800', ring: 'ring-indigo-200' },
  { bg: 'bg-orange-100', text: 'text-orange-800', ring: 'ring-orange-200' },
  { bg: 'bg-cyan-100', text: 'text-cyan-800', ring: 'ring-cyan-200' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', ring: 'ring-fuchsia-200' },
];

function hashClinicianName(name) {
  const s = String(name || '').trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Stable avatar colours per clinician name. */
export function getClinicianAvatarClasses(name) {
  const palette = CLINICIAN_AVATAR_PALETTE[hashClinicianName(name) % CLINICIAN_AVATAR_PALETTE.length];
  return `${palette.bg} ${palette.text} ring-1 ${palette.ring}`;
}

/**
 * Clinician shown in consent list avatar — treating dentist first, else generated-by if a person.
 * @returns {{ name: string|null, initials: string|null, role: 'treating'|'generated'|null, tooltip: string }}
 */
export function resolveConsentTableClinician(consent) {
  if (!consent) {
    return { name: null, initials: null, role: null, tooltip: 'Clinician not recorded' };
  }

  const clinicName = (consent.clinicName || '').trim();
  const treatingName = (consent.dentistInfo?.name || '').trim();
  if (treatingName && !isClinicNameLabel(treatingName, clinicName)) {
    return {
      name: treatingName,
      initials: initialsFromPersonName(treatingName),
      role: 'treating',
      tooltip: `Treating: ${treatingName}`,
    };
  }

  const generatedName = (consent.generatedByInfo?.name || '').trim();
  if (generatedName && !isClinicNameLabel(generatedName, clinicName)) {
    return {
      name: generatedName,
      initials: initialsFromPersonName(generatedName),
      role: 'generated',
      tooltip: `Generated by: ${generatedName}`,
    };
  }

  return { name: null, initials: null, role: null, tooltip: 'Clinician not recorded' };
}

/** True when a label is empty or matches the practice/clinic name (not a person). */
export function isClinicNameLabel(name, clinicName) {
  const n = (name || '').trim();
  if (!n) return true;
  const c = (clinicName || '').trim();
  if (!c) return false;
  return n.toLowerCase() === c.toLowerCase();
}

/** Name shown under clinician signature on consent preview/PDF. */
export function resolveClinicianDisplayName(consent) {
  if (!consent) return '';
  const clinicName = consent.clinicName || '';
  const candidates = [
    consent.clinicianSignature?.clinicianName,
    consent.clinicianSignature?.name,
    // Treating clinician (dropdown) before who generated the form
    consent.dentistInfo?.name,
    consent.generatedByInfo?.name,
    consent.clinicianName,
    consent.createdByName,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim() && !isClinicNameLabel(value, clinicName)) {
      return value.trim();
    }
  }
  return '';
}

/**
 * Treating clinician on the consent — prefer a real person name, fall back to who generated it.
 */
export function resolveConsentDentistInfo(treatingInfo, generatedByInfo, clinicName) {
  const pick = (info) =>
    info?.name && !isClinicNameLabel(info.name, clinicName) ? info : null;
  const treating = pick(treatingInfo);
  const generated = pick(generatedByInfo);
  if (treating) {
    return {
      ...generated,
      ...treating,
      name: treating.name.trim(),
      initials: treating.initials || generated?.initials || '',
      email: treating.email || generated?.email || '',
      userId: treating.userId || generated?.userId || '',
    };
  }
  return generated || generatedByInfo || treatingInfo || null;
}
